import { ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import I18NLink from "@/components/i18n-link";
import { cn, formatNumberToCurrency } from "@/lib/utils";
import type { Product } from "@/lib/products";

export const QuickAddStates = {
  add: "add",
  chooseOptions: "choose_options",
  soldOut: "sold_out",
} as const;

export type QuickAddState =
  (typeof QuickAddStates)[keyof typeof QuickAddStates];

/** Below this many units a card says how many are left. */
const LOW_STOCK_HINT = 5;

/** A product tile in the catalogue grid. */
export function CatalogProductCard({
  product,
  href,
  price,
  isFromPrice,
  quickAddState,
  onQuickAdd,
}: {
  product: Product;
  href: string;
  price: number | undefined;
  isFromPrice: boolean;
  quickAddState: QuickAddState;
  onQuickAdd: () => void;
}) {
  const { t } = useTranslation("translation");
  const image = product.images[0];
  const isSoldOut = quickAddState === QuickAddStates.soldOut;

  return (
    <article className="group flex w-full flex-col">
      <I18NLink
        to={href}
        className="relative block aspect-square overflow-hidden rounded-xl bg-gray-100"
      >
        {image ? (
          <img
            src={image}
            alt={product.name}
            loading="lazy"
            className={cn(
              "size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]",
              isSoldOut && "opacity-60"
            )}
          />
        ) : (
          <div className="flex size-full items-center justify-center text-gray-300">
            <ImageIcon className="size-10" />
          </div>
        )}
        {isSoldOut && (
          <span className="absolute left-2 top-2 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm">
            {t("Storefront.sold_out")}
          </span>
        )}
      </I18NLink>

      <div className="mt-3 flex flex-1 flex-col gap-1">
        <I18NLink
          to={href}
          className="line-clamp-2 text-sm font-medium leading-snug hover:underline sm:text-base"
        >
          {product.name}
        </I18NLink>
        {price != null && (
          <p className="text-sm font-semibold tabular-nums sm:text-base">
            {isFromPrice
              ? t("Storefront.from_price", {
                  price: formatNumberToCurrency({ number: price }),
                })
              : formatNumberToCurrency({ number: price })}
          </p>
        )}
        {!isSoldOut &&
          product.stock != null &&
          product.stock <= LOW_STOCK_HINT && (
            <p className="text-xs font-medium text-amber-700">
              {t("Storefront.only_left", { count: product.stock })}
            </p>
          )}
      </div>

      <button
        type="button"
        onClick={onQuickAdd}
        disabled={isSoldOut}
        className="mt-3 min-h-11 w-full cursor-pointer rounded-full border border-gray-900 px-3 text-sm font-semibold transition-colors hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent"
      >
        {quickAddState === QuickAddStates.chooseOptions
          ? t("Storefront.choose_options")
          : isSoldOut
            ? t("Storefront.sold_out")
            : t("Storefront.add_to_cart")}
      </button>
    </article>
  );
}
